import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { FadeIn } from '@atlaskit/motion';
import Portal from '@atlaskit/portal';
import { Text } from '@atlaskit/primitives/compiled';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	UNSAFE_UpdateOnChange as PopoverUpdateOnChange,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	type Placement,
} from '@atlaskit/spotlight';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { fg } from '@confluence/feature-gating';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const i18n = defineMessages({
	deeplinkReturnTitle: {
		id: 'onboarding-ai-first-create.nudge.deeplink-return.title',
		defaultMessage: 'Get back to creating with Rovo',
		description: 'Title for deeplink return spotlight',
	},
	deeplinkReturnDescription: {
		id: 'onboarding-ai-first-create.nudge.deeplink-return.description',
		defaultMessage: 'Create with Rovo from here whenever you’re ready.',
		description: 'Description text for deeplink return spotlight',
	},
	deeplinkReturnCTA: {
		id: 'onboarding-ai-first-create.nudge.deeplink-return.cta',
		defaultMessage: 'OK',
		description: 'CTA button text for deeplink return spotlight',
	},
});

export type AIFCDeeplinkReturnSpotlightProps = {
	children?: React.ReactNode;
	offset?: [number | null | undefined, number | null | undefined];
	placement?: Placement;
};

export const AIFCDeeplinkReturnSpotlight = ({
	children,
	offset = [0, 8],
	placement = 'bottom-start',
}: AIFCDeeplinkReturnSpotlightProps) => {
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { useChoreographer } = useChoreographerAssets();
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	const { entryPoint, showSmartCreationModal } = useSmartCreationModalState();
	const [capturedEntryPoint, setCapturedEntryPoint] = useState<string | null>(null);

	const { isRovoLLMEnabled } = useSessionData();
	const isLicensed = useIsLicensed();

	const shouldSkip = !isLicensed || !isRovoLLMEnabled;

	const {
		data: onboardingStateData,
		loading: isOnboardingStateLoading,
		error: isOnboardingStateError,
	} = useGetOnboardingState(
		[...Object.values(AIFC_ONBOARDING_STATE_KEYS)],
		shouldSkip || !fg('cc_fd_aifc_deeplink_return_nudge_enabled'),
	);

	const { hasSeenDeeplinkReturnSpotlight } = deserializeState(onboardingStateData);

	// Capture and preserve the entryPoint value when it's a deeplink entry
	useEffect(() => {
		if (!shouldSkip && !hasSeenDeeplinkReturnSpotlight && entryPoint?.includes('atlassianNew')) {
			setCapturedEntryPoint(entryPoint);
		}
	}, [entryPoint, shouldSkip, hasSeenDeeplinkReturnSpotlight]);

	const handleSpotlightStart = useCallback(() => {
		setOnboardingState([
			{
				key: AIFC_ONBOARDING_STATE_KEYS.hasSeenDeeplinkReturnSpotlight,
				value: 'true',
			},
		]);

		setOnboardingState([
			{
				key: AIFC_ONBOARDING_STATE_KEYS.hasSeenAIFCSpotlight,
				value: 'true',
			},
		]);

		setIsSpotlightActive(true);
		setCapturedEntryPoint(null);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'AIFCDeeplinkReturnSpotlight',
				attributes: {
					source: 'deeplinkCreateWithRovo',
				},
			},
		}).fire();
	}, [setOnboardingState, createAnalyticsEvent]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	const choreographer = useChoreographer('AIFCDeeplinkReturnSpotlight', {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const handleDismiss = useCallback(() => {
		void choreographer.stopMessage();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'AIFCDeeplinkReturnSpotlight',
				source: 'deeplinkCreateWithRovo',
			},
		}).fire();
	}, [choreographer, createAnalyticsEvent]);

	useEffect(() => {
		const loadingCompleteWithoutError = !isOnboardingStateLoading && !isOnboardingStateError;

		if (
			loadingCompleteWithoutError &&
			!!capturedEntryPoint &&
			!!onboardingStateData &&
			!hasSeenDeeplinkReturnSpotlight &&
			!showSmartCreationModal
		) {
			void choreographer.startMessage();
		}
	}, [
		capturedEntryPoint,
		choreographer,
		onboardingStateData,
		hasSeenDeeplinkReturnSpotlight,
		isOnboardingStateLoading,
		isOnboardingStateError,
		showSmartCreationModal,
	]);

	if (!fg('cc_fd_aifc_deeplink_return_nudge_enabled')) {
		return children;
	}

	return (
		<PopoverProvider>
			<PopoverUpdateOnChange />
			<PopoverTarget>{children}</PopoverTarget>
			<Portal zIndex={600}>
				<PopoverContent
					dismiss={handleDismiss}
					isVisible={isSpotlightActive}
					placement={placement}
					offset={offset}
				>
					<FadeIn>
						{(props) => (
							<div {...props}>
								<SpotlightCard testId="aifc-deeplink-return-spotlight">
									<SpotlightHeader>
										<SpotlightHeadline>{formatMessage(i18n.deeplinkReturnTitle)}</SpotlightHeadline>
										<SpotlightControls>
											<SpotlightDismissControl />
										</SpotlightControls>
									</SpotlightHeader>
									<SpotlightBody>
										<Text>{formatMessage(i18n.deeplinkReturnDescription)}</Text>
									</SpotlightBody>
									<SpotlightFooter>
										<SpotlightActions>
											<SpotlightPrimaryAction onClick={handleDismiss}>
												{formatMessage(i18n.deeplinkReturnCTA)}
											</SpotlightPrimaryAction>
										</SpotlightActions>
									</SpotlightFooter>
								</SpotlightCard>
							</div>
						)}
					</FadeIn>
				</PopoverContent>
			</Portal>
		</PopoverProvider>
	);
};
